import React from 'react';
import moment from 'moment';
import { CircleAlert } from 'lucide-react';
import { AgnesAlert, AgnesAlertContent, AgnesButton, AgnesSpinner } from '@agnes/ui';
import { EmptyState } from '@/components/ui/EmptyState';
import ServiceRubricaTabela, {
  type RubricaHistoricoAlteracao,
  type RubricaHistoricoItem,
  type RubricaTabelaItem,
} from '@/services/ServiceRubricaTabela';

const dataQa = 'rubricas-tabela-historico';
const PAGE_SIZE = 50;

// Evento fixo da recepção: só a data (dh_recepcao do S-1010) muda.
const RECEPCAO_TITULO = 'Rubrica recebida via S-1010';
const RECEPCAO_ORIGEM = 'Recepção eSocial';

// Com offset (updatedAt, em UTC) converte para o horário local; sem offset (dhRecepcao,
// horário do eSocial) exibe como veio.
const fmtQuando = (v: string | null) => {
  if (!v) return null;
  const m = moment(v, moment.ISO_8601);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : v;
};

// Mesmo aviso da listagem de Afastamento para valor vazio.
const CAMPO_SEM_PREENCHIMENTO = 'Campo sem preenchimento no XML';

const fmtValor = (valor: string | null, descricao: string | null) =>
  descricao ||
  valor || (
    <span className="inline-flex items-center gap-1.5 align-middle text-agnes-error">
      <CircleAlert className="h-4 w-4 shrink-0" /> {CAMPO_SEM_PREENCHIMENTO}
    </span>
  );

const Alteracao = ({ alteracao }: { alteracao: RubricaHistoricoAlteracao }) => (
  <p className="text-sm text-agnes-text">
    <span className="font-semibold">
      {alteracao.changedFieldDescription ?? alteracao.changedField ?? 'Alteração'}:
    </span>{' '}
    {fmtValor(alteracao.fromValue, alteracao.fromValueDescription)} →{' '}
    {fmtValor(alteracao.toValue, alteracao.toValueDescription)}
  </p>
);

type Evento = { key: string; conteudo: React.ReactNode; detalhe: string };

const eventoEdicao = (item: RubricaHistoricoItem): Evento => ({
  key: `edicao-${item.id}`,
  conteudo: item.changes.map((alteracao) => <Alteracao key={alteracao.id} alteracao={alteracao} />),
  detalhe: [item.updatedByName ?? item.updatedBy, fmtQuando(item.updatedAt)]
    .filter(Boolean)
    .join(' · '),
});

const eventoRecepcao = (dhRecepcao: string): Evento => ({
  key: 'recepcao',
  conteudo: <p className="text-sm font-semibold text-agnes-text">{RECEPCAO_TITULO}</p>,
  detalhe: [RECEPCAO_ORIGEM, fmtQuando(dhRecepcao)].filter(Boolean).join(' · '),
});

// Timeline de histórico de recomendações da rubrica. Extraído do modal da CON-5370
// (ModalHistoricoRubrica) para ser apresentado como aba na página de análise (CON-5377).
export function HistoricoRubrica({ rubrica }: { rubrica: RubricaTabelaItem }) {
  // Objeto novo a cada pedido: repetir a mesma página (nova tentativa) refaz a busca.
  const [pedido, setPedido] = React.useState({ page: 0 });
  const [paginasCarregadas, setPaginasCarregadas] = React.useState(0);
  const [rows, setRows] = React.useState<RubricaHistoricoItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [dhRecepcao, setDhRecepcao] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);
    ServiceRubricaTabela.historico(rubrica.idAnaliseRubrica, pedido.page, PAGE_SIZE)
      .then(({ data }) => {
        if (ignore) return;
        const content = data.content ?? [];
        setRows((prev) => (pedido.page === 0 ? content : [...prev, ...content]));
        setTotal(data.totalElements ?? 0);
        setDhRecepcao(data.dhRecepcao ?? null);
        setPaginasCarregadas(pedido.page + 1);
      })
      .catch(() => {
        if (!ignore) setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [rubrica.idAnaliseRubrica, pedido]);

  const carregarMais = () => setPedido({ page: paginasCarregadas });
  const temMais = rows.length < total;

  // A recepção é o evento mais antigo: entra no fim, depois da última página.
  const eventos = rows.map(eventoEdicao);
  if (paginasCarregadas > 0 && !temMais && dhRecepcao) eventos.push(eventoRecepcao(dhRecepcao));

  let conteudo: React.ReactNode;
  if (eventos.length === 0) {
    if (error) {
      conteudo = (
        <AgnesAlert preset="error">
          <AgnesAlertContent>Não foi possível carregar o histórico da rubrica.</AgnesAlertContent>
        </AgnesAlert>
      );
    } else if (loading) {
      conteudo = (
        <div className="flex items-center gap-2 py-6 text-sm text-agnes-text-muted">
          <AgnesSpinner size="sm" />
          Carregando histórico...
        </div>
      );
    } else {
      conteudo = <EmptyState title="Nenhuma alteração registrada" />;
    }
  } else {
    conteudo = (
      <>
        <ol data-qa={`${dataQa}-timeline`}>
          {eventos.map((evento, index) => {
            const ultimo = index === eventos.length - 1;
            return (
              <li key={evento.key} data-qa={`${dataQa}-${evento.key}`} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    aria-hidden
                    className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                      index === 0 ? 'bg-agnes-success' : 'bg-agnes-info-fluorescent'
                    }`}
                  />
                  {!ultimo && <span aria-hidden className="mt-1 w-px flex-1 bg-agnes-border" />}
                </div>
                <div className={ultimo ? undefined : 'pb-4'}>
                  {evento.conteudo}
                  <p className="mt-0.5 text-xs text-agnes-text-muted">{evento.detalhe}</p>
                </div>
              </li>
            );
          })}
        </ol>
        {error && (
          <p className="mt-3 text-sm text-agnes-error">Não foi possível carregar mais registros.</p>
        )}
        {temMais && (
          <div className="mt-4 flex justify-center">
            <AgnesButton
              data-qa={`${dataQa}-carregar-mais`}
              variant="outlined"
              size="sm"
              loading={loading}
              disabled={loading}
              onClick={carregarMais}
            >
              {error ? 'Tentar novamente' : 'Carregar mais'}
            </AgnesButton>
          </div>
        )}
      </>
    );
  }

  return <div className="py-2">{conteudo}</div>;
}
